import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from './components/ui/sonner';
import { useOrderStore } from './store';
import OrderTrackingBanner from './components/OrderTrackingBanner';
import MyOrders from './pages/customer/MyOrders';
import Home from './pages/Home';
import TableEntry from './pages/customer/TableEntry';
import ScanTableEntry from './pages/customer/ScanTableEntry';
import Menu from './pages/customer/Menu';
import Cart from './pages/customer/Cart';
import OrderConfirmation from './pages/customer/OrderConfirmation';
import TipPage from './pages/customer/TipPage';
import Login from './pages/auth/Login';
import ResetPassword from './pages/auth/ResetPassword';
import ForgotPassword from './pages/auth/ForgotPassword';
import AcceptInvite from './pages/auth/AcceptInvite';
import ProtectedRoute from './components/routes/ProtectedRoute';
import AdminRoute from './components/routes/AdminRoute';
import StaffRoute from './components/routes/StaffRoute';
import SuperAdminRoute from './components/routes/SuperAdminRoute';

import StaffDashboard from './pages/staff/Dashboard';
import EstablishmentEntry from './pages/customer/EstablishmentEntry';
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/Dashboard';
import AdminMenu from './pages/admin/Menu';
import AdminTables from './pages/admin/Tables';
import AdminHappyHours from './pages/admin/HappyHours';
import AdminPromoCodes from './pages/admin/PromoCodes';
import AdminStaff from './pages/admin/Staff';
import AdminOrders from './pages/admin/Orders';
import AdminAnalytics from './pages/admin/Analytics';
import AdminSettings from './pages/admin/Settings';
import AdminBilling from './pages/admin/Billing';
import SuperAdminDashboard from './pages/superadmin/Dashboard';

import './App.css';
import useViewportSize from './hooks/useViewportSize';
import PortalSession from './components/routes/PortalSession';
import { CustomerLanguageBar } from './components/LanguageSelector';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false
    }
  }
});

const CustomerTrackingBanner = () => {
  const location = useLocation();
  const activeOrderId = useOrderStore((state) => state.activeOrderId);

  const isCustomerRoute =
    ['/menu', '/cart', '/tip', '/order-confirmation'].some(
      (r) => location.pathname === r || location.pathname.startsWith(r)
    ) ||
    location.pathname.startsWith('/table/') ||
    location.pathname.startsWith('/scan/');

  if (!isCustomerRoute || !activeOrderId) return null;

  return <OrderTrackingBanner />;
};

function App() {
  useViewportSize();
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <PortalSession>
        <div className="glassorder-skin">
          <CustomerLanguageBar />
          <CustomerTrackingBanner />

          <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/entry/:establishmentId" element={<EstablishmentEntry />} />
          <Route path="/restaurant/:establishmentId" element={<EstablishmentEntry />} />
          <Route path="/table/:tableId" element={<TableEntry />} />
          <Route path="/scan/:establishmentId/:tableNumber" element={<ScanTableEntry />} />
          <Route path="/menu" element={<Menu />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/order-confirmation" element={<OrderConfirmation />} />
          <Route path="/tip" element={<TipPage />} />
          <Route path="/my-orders" element={<MyOrders />} />
          <Route path="/staff/login" element={<Login />} />
          <Route path="/admin/login" element={<Login />} />
          <Route path="/superadmin/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route element={<ProtectedRoute />}>
            <Route element={<StaffRoute />}>
              <Route path="/staff/dashboard" element={<StaffDashboard />} />
            </Route>

            <Route element={<AdminRoute />}>
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<Navigate to="/admin/dashboard" replace />} />
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="menu" element={<AdminMenu />} />
                <Route path="tables" element={<AdminTables />} />
                <Route path="happy-hours" element={<AdminHappyHours />} />
                <Route path="promo-codes" element={<AdminPromoCodes />} />
                <Route path="staff" element={<AdminStaff />} />
                <Route path="orders" element={<AdminOrders />} />
                <Route path="analytics" element={<AdminAnalytics />} />
                <Route path="settings" element={<AdminSettings />} />
                <Route path="billing" element={<AdminBilling />} />
              </Route>
            </Route>

            <Route element={<SuperAdminRoute />}>
              <Route path="/superadmin/dashboard" element={<SuperAdminDashboard />} />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
        </PortalSession>
      </BrowserRouter>

      <Toaster position="top-center" richColors />
    </QueryClientProvider>
  );
}

export default App;
